/**
 * CommunitiesHero — "Find your people." landing hero with a live search
 * field and a floating composition of conversations, avatars and books.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, MessageCircle, Users } from 'lucide-react';
import { Magnetic } from '@/components/site/primitives';

const EASE = [0.22, 1, 0.36, 1];
const soft = { hidden: { opacity: 0, y: 20 }, show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE, delay: i * 0.1 } }) };
const pop = { hidden: { opacity: 0, scale: 0.7 }, show: (i) => ({ opacity: 1, scale: 1, transition: { duration: 0.6, ease: EASE, delay: 0.5 + i * 0.12 } }) };

const spineTone = {
  coral: 'from-coral to-[#b85e58]',
  yellow: 'from-yellow to-[#e0b164]',
  sage: 'from-sage to-[#a9c39d]',
  blue: 'from-blue to-[#6b808b]',
};

export default function CommunitiesHero({ searchValue, onSearchChange, onCreateClick }) {
  return (
    <section className="relative overflow-hidden pt-24 md:pt-32" data-testid="communities-hero">
      <div className="pointer-events-none absolute right-[4%] top-16 h-[460px] w-[460px] rounded-full" aria-hidden="true">
        <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle at 45% 40%, rgba(240,199,127,0.35), rgba(240,199,127,0) 68%)' }} />
      </div>

      <div className="u-container relative grid items-center gap-10 pb-16 md:grid-cols-[1.05fr_0.95fr] md:pb-24">
        {/* Left: copy + search */}
        <div className="relative z-10">
          <motion.span variants={soft} custom={0} initial="hidden" animate="show" className="u-eyebrow mb-5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> Come for the books, stay for the people
          </motion.span>

          <motion.h1 variants={soft} custom={1} initial="hidden" animate="show" className="font-display text-[clamp(2.5rem,6vw,4.25rem)] font-semibold leading-[1.02] tracking-tight text-ink">
            Find your <span className="italic text-coral">reading people.</span>
          </motion.h1>

          <motion.p variants={soft} custom={2} initial="hidden" animate="show" className="mt-6 max-w-md text-lg leading-relaxed text-subtle">
            Discover communities built around the stories, genres and ideas you love — and the readers and writers who love them too.
          </motion.p>

          <motion.div variants={soft} custom={3} initial="hidden" animate="show" className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
              <input
                type="text"
                value={searchValue}
                onChange={e => onSearchChange(e.target.value)}
                placeholder="Search communities…"
                className="w-full rounded-full border border-line bg-card py-3 pl-11 pr-4 text-sm text-ink shadow-soft outline-none transition-colors placeholder:text-subtle focus:border-coral"
              />
            </div>
            <Magnetic strength={0.2}>
              <button
                type="button"
                onClick={onCreateClick}
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-card px-5 py-3 text-sm font-semibold text-ink shadow-soft transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:border-coral/50 hover:text-coral"
              >
                <Plus className="h-4 w-4" /> Create Community
              </button>
            </Magnetic>
          </motion.div>

          <motion.div variants={soft} custom={4} initial="hidden" animate="show" className="mt-7 flex items-center gap-3 text-sm text-subtle">
            <div className="flex -space-x-2">
              {['coral', 'sage', 'yellow', 'blue'].map((t, i) => (
                <span key={i} className="h-7 w-7 rounded-full border-2 border-cream" style={{ background: `var(--color-${t})` }} />
              ))}
            </div>
            <span><span className="font-semibold text-ink">340+</span> communities, always mid-conversation</span>
          </motion.div>
        </div>

        {/* Right: floating composition */}
        <div className="relative z-10 mx-auto hidden w-full max-w-[460px] md:block">
          <div className="relative h-[420px]">
            <motion.div
              variants={pop} custom={0} initial="hidden" animate="show"
              className="absolute left-4 top-4 w-72 -rotate-2 animate-floatSlow rounded-2xl rounded-tl-md border border-line bg-card p-4 shadow-card"
            >
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-coral/15 text-xs font-bold text-coral">P</span>
                <p className="text-xs font-semibold text-ink">Fantasy Readers</p>
                <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-subtle"><Users className="h-3 w-3" /> 2.4k</span>
              </div>
              <p className="mt-2.5 font-display text-[15px] leading-snug text-ink">"What fantasy book completely changed your expectations of the genre?"</p>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-subtle">
                <MessageCircle className="h-3 w-3" /> 42 replies and counting
              </div>
            </motion.div>

            <motion.div
              variants={pop} custom={1} initial="hidden" animate="show"
              className="absolute right-0 top-40 animate-float rounded-full border border-line bg-card px-3.5 py-2 shadow-soft"
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-sage" /></span>
                186 active now
              </span>
            </motion.div>

            <motion.div
              variants={pop} custom={2} initial="hidden" animate="show"
              className="absolute bottom-10 left-10 flex animate-floatSlow items-center gap-2 rounded-full border border-line bg-card px-3 py-2 shadow-soft"
            >
              <div className="flex -space-x-2" aria-hidden="true">
                {['coral', 'yellow', 'sage'].map((t, i) => (
                  <span key={i} className={`h-8 w-6 rounded-[3px] bg-gradient-to-br shadow-sm ring-2 ring-card ${spineTone[t]}`} />
                ))}
              </div>
              <span className="text-[11px] font-semibold text-subtle">Worldbuilding Club</span>
            </motion.div>

            <motion.div
              variants={pop} custom={3} initial="hidden" animate="show"
              className="absolute bottom-0 right-6 animate-float rotate-3 rounded-2xl rounded-br-md border border-line bg-coral-fill px-4 py-2.5 text-[#FFF7F0] shadow-card"
            >
              <p className="text-xs font-medium">"Currently reading The Night Circus 🎪"</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
